import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import type {PageResponse} from "~/services/ApiTypes";
import {
    chainEntitlementUrl,
    chainPendingCreditRequestsUrl,
    chainUnlockListUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ChainCreditEntitlement,
    ChainCreditRequest,
    ChainUnlock,
} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";

/**
 * The databag's credit entitlement (`GET supplier_360/chain_entitlement/?databag_id=`, §2.14).
 * Resolves to `null` when the databag has none — the "not enabled" case, not an error.
 */
export function useChainEntitlementQuery(databagId: number | undefined) {
    const url = chainEntitlementUrl(databagId ?? 0);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => {
            const resp = (await queryHttp.get<PageResponse<ChainCreditEntitlement>>(url)).data;
            return resp.results[0] ?? null;
        },
        enabled: Boolean(databagId),
    });
}

/** Every supplier unlocked under one entitlement (§2.15) — also how "is this supplier unlocked" is answered. */
export function useChainUnlocksQuery(entitlementId: number | undefined) {
    const url = chainUnlockListUrl(entitlementId ?? 0);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<ChainUnlock[]>(url)).data,
        enabled: Boolean(entitlementId),
    });
}

/** Pending credit requests under one entitlement (§2.16), newest first. */
export function useChainPendingCreditRequestsQuery(entitlementId: number | undefined) {
    const url = chainPendingCreditRequestsUrl(entitlementId ?? 0);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<ChainCreditRequest[]>(url)).data,
        enabled: Boolean(entitlementId),
    });
}
